import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Chip,
  Paper,
  InputAdornment,
  Alert,
  Snackbar,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  IconButton,
  Collapse,
  Badge,
  Drawer,
  Tooltip,
} from '@mui/material';
import {
  Search,
  GridView,
  ViewList,
  FormatPaint,
  AddShoppingCart,
  TuneOutlined,
  ExpandMore,
  ExpandLess,
  SwapVert,
  CloseOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Product, GroupedProduct, ColorSwatch } from '../types';
import { useApiCart } from '../hooks/useApiCartProvider';
import { useGroupedProducts } from '../hooks/useGroupedProducts';
import { productService } from '../services';
import { promotionService, PromotionBanner } from '../services/promotionService';
import { trackEvent } from '../services/analytics';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ProductSearchAutocomplete from '../components/products/ProductSearchAutocomplete';

// ─── Format helpers ───────────────────────────────────────────

const formatPrice = (price: number | null | undefined) => {
  if (!price) return '-';
  return `Rp ${price.toLocaleString('id-ID')}`;
};

// ─── Product Card (Grid View) ─────────────────────────────────

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product, quantity: number) => Promise<void>;
  onProductClick?: (productId: string) => void;
}

const ProductCardGrid: React.FC<ProductCardProps> = ({ product, onAddToCart, onProductClick }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  // Use promotion_discount for display (Epicor customer discount only at cart level)
  const promoDiscount = (product as any).promotion_discount;
  const hasDiscount = promoDiscount && promoDiscount > 0;
  const originalPrice = product.unit_price;
  const finalPrice = hasDiscount
    ? Math.round((originalPrice || 0) * (1 - promoDiscount / 100))
    : (product.discounted_price || product.unitPrice || product.unit_price);

  const categoryLabel = product.family_name && product.subfamily_name && product.family_name !== product.subfamily_name
    ? `${product.family_name} · ${product.subfamily_name}`
    : product.family_name || product.subfamily_name || '';

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          borderColor: 'grey.300',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Image container */}
      <Box
        onClick={() => onProductClick?.(product.id)}
        sx={{
          position: 'relative',
          height: { xs: 220, sm: 240, md: 260 },
          bgcolor: '#f8f9fa',
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={product.image_url || '/product-placeholder.svg'}
          alt={product.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 1,
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />

        {/* Discount badge */}
        {hasDiscount && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: '#ff424e',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.75rem',
              px: 1,
              py: 0.3,
              borderRadius: 1,
              lineHeight: 1.3,
            }}
          >
            -{Number.isInteger(promoDiscount) ? promoDiscount : promoDiscount.toFixed(1)}%
          </Box>
        )}

        {/* Quick add overlay on hover */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(8px)',
            transition: 'all 0.25s ease',
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<AddShoppingCart sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onAddToCart(product, 1); }}
            sx={{
              bgcolor: 'white',
              color: '#333',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderRadius: 5,
              px: 2,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            {t('products.addToCart')}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <CardContent sx={{ flexGrow: 1, p: 2, pb: '12px !important' }}>
        {/* Category */}
        {categoryLabel && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              textTransform: 'uppercase',
              fontSize: '0.6rem',
              letterSpacing: 0.8,
              fontWeight: 500,
              display: 'block',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {categoryLabel}
          </Typography>
        )}

        {/* Product name */}
        <Typography
          variant="body2"
          onClick={() => onProductClick?.(product.id)}
          sx={{
            fontWeight: 500,
            color: '#333',
            lineHeight: 1.4,
            mb: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.8em',
            cursor: 'pointer',
            '&:hover': { color: '#ee4d2d' },
          }}
        >
          {product.name}
        </Typography>

        {/* UOM */}
        {product.unit_of_measure && (
          <Chip
            label={product.unit_of_measure}
            size="small"
            sx={{
              fontSize: '0.65rem',
              height: 20,
              bgcolor: '#f5f5f5',
              color: 'text.secondary',
              mb: 1,
            }}
          />
        )}

        {/* Price section */}
        <Box sx={{ mt: 'auto' }}>
          {hasDiscount && originalPrice ? (
            <>
              <Typography
                variant="caption"
                sx={{
                  color: '#999',
                  textDecoration: 'line-through',
                  fontSize: '0.75rem',
                  display: 'block',
                  lineHeight: 1,
                }}
              >
                {formatPrice(originalPrice)}
              </Typography>
              <Typography
                sx={{
                  color: '#ee4d2d',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: 1.3,
                }}
              >
                {formatPrice(finalPrice)}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                color: '#222',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.3,
              }}
            >
              {formatPrice(finalPrice)}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Product Row (List View) ──────────────────────────────────

const ProductCardList: React.FC<ProductCardProps> = ({ product, onAddToCart, onProductClick }) => {
  const { t } = useTranslation();
  const promoDiscount = (product as any).promotion_discount;
  const hasDiscount = promoDiscount && promoDiscount > 0;
  const originalPrice = product.unit_price;
  const finalPrice = hasDiscount
    ? Math.round((originalPrice || 0) * (1 - promoDiscount / 100))
    : (product.discounted_price || product.unitPrice || product.unit_price);
  const [quantity, setQuantity] = useState(1);

  const categoryLabel = product.family_name && product.subfamily_name && product.family_name !== product.subfamily_name
    ? `${product.family_name} · ${product.subfamily_name}`
    : product.family_name || product.subfamily_name || '';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        mb: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        bgcolor: 'white',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
      }}
    >
      {/* Image */}
      <Box
        onClick={() => onProductClick?.(product.id)}
        sx={{
          position: 'relative',
          width: { xs: 80, sm: 100, md: 120 },
          height: { xs: 80, sm: 100, md: 120 },
          flexShrink: 0,
          bgcolor: '#f8f9fa',
          borderRadius: 1.5,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <Box
          component="img"
          src={product.image_url || '/product-placeholder.svg'}
          alt={product.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 1,
          }}
        />
        {hasDiscount && (
          <Box sx={{
            position: 'absolute', top: 4, left: 4, bgcolor: '#ff424e', color: 'white',
            fontWeight: 700, fontSize: '0.6rem', px: 0.5, py: 0.2, borderRadius: 0.5,
          }}>
            -{Number.isInteger(promoDiscount) ? promoDiscount : promoDiscount.toFixed(1)}%
          </Box>
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {categoryLabel && (
          <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
            {categoryLabel}
          </Typography>
        )}
        <Typography
          variant="body2"
          fontWeight={500}
          onClick={() => onProductClick?.(product.id)}
          sx={{
            mb: 0.5,
            cursor: 'pointer',
            '&:hover': { color: '#ee4d2d' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
          }}
        >
          {product.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          {product.unit_of_measure && (
            <Chip label={product.unit_of_measure} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f5f5f5' }} />
          )}
        </Box>
        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          {hasDiscount && originalPrice && (
            <Typography variant="caption" sx={{ color: '#999', textDecoration: 'line-through', fontSize: '0.75rem' }}>
              {formatPrice(originalPrice)}
            </Typography>
          )}
          <Typography sx={{ color: hasDiscount ? '#ee4d2d' : '#222', fontWeight: 700, fontSize: '0.95rem' }}>
            {formatPrice(finalPrice)}
          </Typography>
        </Box>
      </Box>

      {/* Quantity + Cart */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
          <Button size="small" sx={{ minWidth: 28, px: 0 }} onClick={() => setQuantity((q) => Math.max(1, q - 1))} disabled={quantity <= 1}>-</Button>
          <Typography sx={{ px: 0.5, minWidth: 24, textAlign: 'center', fontSize: '0.8rem' }}>{quantity}</Typography>
          <Button size="small" sx={{ minWidth: 28, px: 0 }} onClick={() => setQuantity((q) => q + 1)}>+</Button>
        </Box>
        <IconButton
          color="primary"
          onClick={() => onAddToCart(product, quantity)}
          sx={{ bgcolor: '#ee4d2d', color: 'white', '&:hover': { bgcolor: '#d73211' }, width: 36, height: 36 }}
        >
          <AddShoppingCart sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Color Swatch Row ────────────────────────────────────────

const ColorSwatchRow: React.FC<{
  swatches: ColorSwatch[];
  maxVisible?: number;
  size?: number;
}> = ({ swatches, maxVisible = 8, size = 24 }) => {
  const visible = swatches.slice(0, maxVisible);
  const remaining = swatches.length - maxVisible;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 0.75, flexWrap: 'wrap' }}>
      {visible.map((swatch, idx) => (
        <Tooltip
          key={idx}
          title={swatch.color_name || ''}
          arrow
          enterDelay={300}
        >
          <Box
            sx={{
              width: size,
              height: size,
              borderRadius: '50%',
              border: '1.5px solid',
              borderColor: 'grey.300',
              flexShrink: 0,
              overflow: 'hidden',
              bgcolor: swatch.color_hex || '#f5f5f5',
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
              cursor: 'pointer',
              transition: 'transform 0.15s, border-color 0.15s',
              '&:hover': {
                transform: 'scale(1.15)',
                borderColor: '#ee4d2d',
              },
              ...(swatch.color_image_url ? {
                backgroundImage: `url(${swatch.color_image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              } : {}),
            }}
          />
        </Tooltip>
      ))}
      {remaining > 0 && (
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: '1.5px solid',
            borderColor: 'grey.300',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 600 }}>
            +{remaining}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Grouped Product Card (Grid View) ────────────────────────

interface GroupedCardProps {
  group: GroupedProduct;
  onGroupClick: (groupKey: string) => void;
}

const GroupedProductCardGrid: React.FC<GroupedCardProps> = ({ group, onGroupClick }) => {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const [hovered, setHovered] = useState(false);

  const hasPromo = group.promotion_discount && group.promotion_discount > 0;

  const priceDisplay = (() => {
    const minP = group.min_price;
    const maxP = group.max_price;
    if (!minP && !maxP) return '-';
    if (minP === maxP || !maxP) return formatPrice(minP);
    return `${formatPrice(minP)} - ${formatPrice(maxP)}`;
  })();

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.25s ease',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
          borderColor: 'grey.300',
          transform: 'translateY(-2px)',
        },
      }}
      onClick={() => onGroupClick(group.group_key)}
    >
      {/* Image container */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 220, sm: 240, md: 260 },
          bgcolor: '#f8f9fa',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={group.representative_image_url || '/product-placeholder.svg'}
          alt={group.product_name || ''}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            p: 1,
            transition: 'transform 0.4s ease',
            transform: hovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />

        {/* Finish type badge (top-left) */}
        {group.finish_type && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.65rem',
              px: 1,
              py: 0.3,
              borderRadius: 1,
              lineHeight: 1.3,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {group.finish_type}
          </Box>
        )}

        {/* Promo discount badge (top-right) */}
        {hasPromo && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: '#ff424e',
              color: 'white',
              fontWeight: 700,
              fontSize: '0.75rem',
              px: 1,
              py: 0.3,
              borderRadius: 1,
              lineHeight: 1.3,
            }}
          >
            -{Number.isInteger(group.promotion_discount!) ? group.promotion_discount : group.promotion_discount!.toFixed(1)}%
          </Box>
        )}

        {/* Quick action overlay on hover */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(8px)',
            transition: 'all 0.25s ease',
          }}
        >
          <Button
            variant="contained"
            size="small"
            startIcon={<AddShoppingCart sx={{ fontSize: 16 }} />}
            onClick={(e) => { e.stopPropagation(); onGroupClick(group.group_key); }}
            sx={{
              bgcolor: 'white',
              color: '#333',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              borderRadius: 5,
              px: 2,
              '&:hover': { bgcolor: '#f5f5f5' },
            }}
          >
            {isId ? 'Pilih Warna' : 'Choose Color'}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <CardContent sx={{ flexGrow: 1, p: 2, pb: '12px !important' }}>
        {/* Color swatches */}
        {group.color_swatches.length > 0 && (
          <ColorSwatchRow swatches={group.color_swatches} />
        )}

        {/* Category label */}
        {group.category_label && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              textTransform: 'uppercase',
              fontSize: '0.6rem',
              letterSpacing: 0.8,
              fontWeight: 500,
              display: 'block',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {group.category_label}
          </Typography>
        )}

        {/* Product name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: '#333',
            lineHeight: 1.4,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            minHeight: '2.8em',
          }}
        >
          {group.product_name || group.subfamily_name || ''}
        </Typography>

        {/* UOM chip */}
        {group.sales_uom && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
              {isId ? 'Ukuran:' : 'Size:'}
            </Typography>
            <Chip
              label={group.sales_uom}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.65rem',
                height: 20,
                borderColor: 'grey.300',
                color: 'text.secondary',
              }}
            />
          </Box>
        )}

        {/* Variant count */}
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          {group.variant_count} {isId ? 'pilihan warna tersedia' : 'color options available'}
        </Typography>

        {/* Price section */}
        <Box sx={{ mt: 'auto' }}>
          <Typography
            sx={{
              color: hasPromo ? '#ee4d2d' : '#222',
              fontWeight: 700,
              fontSize: '0.95rem',
              lineHeight: 1.3,
            }}
          >
            {priceDisplay}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

// ─── Grouped Product Row (List View) ─────────────────────────

const GroupedProductCardList: React.FC<GroupedCardProps> = ({ group, onGroupClick }) => {
  const { i18n } = useTranslation();
  const isId = i18n.language === 'id';

  const hasPromo = group.promotion_discount && group.promotion_discount > 0;

  const priceDisplay = (() => {
    const minP = group.min_price;
    const maxP = group.max_price;
    if (!minP && !maxP) return '-';
    if (minP === maxP || !maxP) return formatPrice(minP);
    return `${formatPrice(minP)} - ${formatPrice(maxP)}`;
  })();

  return (
    <Box
      onClick={() => onGroupClick(group.group_key)}
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        mb: 1,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 2,
        bgcolor: 'white',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { borderColor: 'grey.300', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
      }}
    >
      {/* Image */}
      <Box
        sx={{
          position: 'relative',
          width: { xs: 80, sm: 100, md: 120 },
          height: { xs: 80, sm: 100, md: 120 },
          flexShrink: 0,
          bgcolor: '#f8f9fa',
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={group.representative_image_url || '/product-placeholder.svg'}
          alt={group.product_name || ''}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 1 }}
        />
        {/* Finish type badge */}
        {group.finish_type && (
          <Box sx={{
            position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(0,0,0,0.6)', color: 'white',
            fontWeight: 600, fontSize: '0.55rem', px: 0.5, py: 0.15, borderRadius: 0.5,
            textTransform: 'uppercase',
          }}>
            {group.finish_type}
          </Box>
        )}
        {hasPromo && (
          <Box sx={{
            position: 'absolute', top: 4, right: 4, bgcolor: '#ff424e', color: 'white',
            fontWeight: 700, fontSize: '0.6rem', px: 0.5, py: 0.2, borderRadius: 0.5,
          }}>
            -{Number.isInteger(group.promotion_discount!) ? group.promotion_discount : group.promotion_discount!.toFixed(1)}%
          </Box>
        )}
      </Box>

      {/* Info */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        {group.category_label && (
          <Typography variant="caption" sx={{ color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: 0.8 }}>
            {group.category_label}
          </Typography>
        )}
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
          }}
        >
          {group.product_name || group.subfamily_name || ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5, alignItems: 'center' }}>
          {group.sales_uom && (
            <Chip label={group.sales_uom} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 18, borderColor: 'grey.300' }} />
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            {group.variant_count} {isId ? 'warna' : 'colors'}
          </Typography>
        </Box>
        {/* Color swatches */}
        {group.color_swatches.length > 0 && (
          <ColorSwatchRow swatches={group.color_swatches} maxVisible={12} />
        )}
        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography sx={{ color: hasPromo ? '#ee4d2d' : '#222', fontWeight: 700, fontSize: '0.95rem' }}>
            {priceDisplay}
          </Typography>
        </Box>
      </Box>

      {/* Action */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddShoppingCart sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onGroupClick(group.group_key); }}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            borderColor: '#ee4d2d',
            color: '#ee4d2d',
            borderRadius: 2,
            '&:hover': { bgcolor: '#fff3f0', borderColor: '#d73211' },
            display: { xs: 'none', sm: 'flex' },
          }}
        >
          {isId ? 'Pilih Warna' : 'Choose Color'}
        </Button>
        <IconButton
          onClick={(e) => { e.stopPropagation(); onGroupClick(group.group_key); }}
          sx={{
            bgcolor: '#ee4d2d', color: 'white',
            '&:hover': { bgcolor: '#d73211' },
            width: 36, height: 36,
            display: { xs: 'flex', sm: 'none' },
          }}
        >
          <AddShoppingCart sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Sidebar Filters ──────────────────────────────────────────

interface FilterOption { code: string; name: string; count?: number }

interface SidebarFiltersProps {
  brands: FilterOption[];
  categoryItems: FilterOption[];
  families: FilterOption[];
  subfamilies: FilterOption[];
  brandFilter: string;
  categoryItemFilter: string;
  familyFilter: string;
  subfamilyFilter: string;
  onBrandChange: (value: string) => void;
  onCategoryItemChange: (value: string) => void;
  onFamilyChange: (value: string) => void;
  onSubfamilyChange: (value: string) => void;
  onReset: () => void;
  activeFilterCount: number;
}

/** Collapsible radio filter section */
const FilterSection: React.FC<{
  title: string;
  options: FilterOption[];
  value: string;
  onChange: (v: string) => void;
  defaultOpen?: boolean;
  maxHeight?: number;
  filterSearch?: string;
}> = ({ title, options, value, onChange, defaultOpen = true, maxHeight = 240, filterSearch }) => {
  const { i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const [open, setOpen] = useState(defaultOpen);

  const filtered = filterSearch
    ? options.filter((o) => o.name.toLowerCase().includes(filterSearch.toLowerCase()))
    : options;

  return (
    <>
      <Divider />
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.50' },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
          {title}
        </Typography>
        {open ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5, maxHeight, overflowY: 'auto' }}>
          <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
            <FormControlLabel
              value=""
              control={<Radio size="small" sx={{ p: 0.5 }} />}
              label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{isId ? 'Semua' : 'All'}</Typography>}
              sx={{ mx: 0, '& .MuiFormControlLabel-label': { ml: 0.5 } }}
            />
            {filtered.map((o) => (
              <FormControlLabel
                key={o.code}
                value={o.code}
                control={<Radio size="small" sx={{ p: 0.5 }} />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.name}
                    </Typography>
                    {o.count !== undefined && (
                      <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem', ml: 0.5, flexShrink: 0 }}>
                        ({o.count})
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ mx: 0, '& .MuiFormControlLabel-label': { ml: 0.5, flexGrow: 1, minWidth: 0 } }}
              />
            ))}
          </RadioGroup>
        </Box>
      </Collapse>
    </>
  );
};

const SidebarFilters: React.FC<SidebarFiltersProps> = ({
  brands,
  categoryItems,
  families,
  subfamilies,
  brandFilter,
  categoryItemFilter,
  familyFilter,
  subfamilyFilter,
  onBrandChange,
  onCategoryItemChange,
  onFamilyChange,
  onSubfamilyChange,
  onReset,
  activeFilterCount,
}) => {
  const { i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const [filterSearch, setFilterSearch] = useState('');

  return (
    <Paper
      sx={{
        borderRadius: 2,
        position: 'sticky',
        top: 80,
        border: '1px solid',
        borderColor: 'grey.200',
        boxShadow: 'none',
        overflow: 'hidden',
        maxHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700}>
            {isId ? 'Filter' : 'Filters'}
          </Typography>
          {activeFilterCount > 0 && (
            <Badge badgeContent={activeFilterCount} color="error" sx={{ ml: 0.5 }} />
          )}
        </Box>
        {activeFilterCount > 0 && (
          <Button
            size="small"
            onClick={onReset}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#ee4d2d' }}
          >
            {isId ? 'Hapus Semua' : 'Clear All'}
          </Button>
        )}
      </Box>

      {/* Filter search */}
      <Box sx={{ px: 2, py: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={isId ? 'Cari filter...' : 'Search filters...'}
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 0.5, fontSize: 18, color: 'text.disabled' }} />,
            sx: { fontSize: '0.8rem', bgcolor: '#f8f9fa', '& fieldset': { border: 'none' }, borderRadius: 1.5 },
          }}
        />
      </Box>

      {/* Scrollable filter sections */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Brand */}
        {brands.length > 0 && (
          <FilterSection
            title={isId ? 'Merek' : 'Brand'}
            options={brands}
            value={brandFilter}
            onChange={onBrandChange}
            filterSearch={filterSearch}
          />
        )}

        {/* Type (CategoryItem) */}
        {categoryItems.length > 0 && (
          <FilterSection
            title={isId ? 'Tipe' : 'Type'}
            options={categoryItems}
            value={categoryItemFilter}
            onChange={onCategoryItemChange}
            filterSearch={filterSearch}
          />
        )}

        {/* Category (Family) */}
        {families.length > 0 && (
          <FilterSection
            title={isId ? 'Kategori' : 'Category'}
            options={families}
            value={familyFilter}
            onChange={onFamilyChange}
            filterSearch={filterSearch}
          />
        )}

        {/* Sub-Category (Subfamily) — always visible when options exist */}
        {subfamilies.length > 0 && (
          <FilterSection
            title={isId ? 'Sub Kategori' : 'Sub-Category'}
            options={subfamilies}
            value={subfamilyFilter}
            onChange={onSubfamilyChange}
            defaultOpen={!familyFilter}
            filterSearch={filterSearch}
          />
        )}
      </Box>
    </Paper>
  );
};

// ─── Variant Picker Dialog (lazy) ──────────────────────────────
const GroupedProductDetailDialog = React.lazy(() => import('../components/products/GroupedProductDetailDialog'));

// ─── Main Page ────────────────────────────────────────────────

const ProductsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isId = i18n.language === 'id';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { addToCart: addToApiCart } = useApiCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    groups: apiGroups,
    loadGroups,
    isLoading: loading,
    error,
    total: apiTotal,
  } = useGroupedProducts();

  // Promotions banner
  const [promoBanners, setPromoBanners] = useState<PromotionBanner[]>([]);
  const [promoIndex, setPromoIndex] = useState(0);

  useEffect(() => {
    promotionService.getActivePromotions()
      .then(setPromoBanners)
      .catch(() => {});
    // GA4 standard e-commerce event -- top-of-funnel signal that
    // fires once per mount when the customer opens the catalog.
    trackEvent('view_item_list');
  }, []);

  // Auto-advance promo carousel
  useEffect(() => {
    if (promoBanners.length <= 1) return;
    const interval = setInterval(() => {
      setPromoIndex((prev) => (prev + 1) % promoBanners.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [promoBanners.length]);

  // Filters — initialize search from URL query param
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryItemFilter, setCategoryItemFilter] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [subfamilyFilter, setSubfamilyFilter] = useState('');

  // Store selected filter names so chips display correctly even when filter results are empty
  const [filterNames, setFilterNames] = useState<Record<string, string>>({});
  const [sortBy, setSortBy] = useState('name-asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Product detail dialog
  const [detailProductId, setDetailProductId] = useState<string | null>(null);
  // Optional preselected variant (used when a user picks a specific variant
  // from the search autocomplete — the dialog should open on that variant
  // instead of defaulting to the first one in the group).
  const [detailInitialVariantId, setDetailInitialVariantId] = useState<string | null>(null);

  // Sync search query from URL when navigating from header search
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch && urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load groups
  useEffect(() => {
    loadGroups({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      brand_code: brandFilter || undefined,
      category_item_code: categoryItemFilter || undefined,
      family_code: familyFilter || undefined,
      subfamily_code: subfamilyFilter || undefined,
      search: searchQuery || undefined,
    });
  }, [loadGroups, page, rowsPerPage, brandFilter, categoryItemFilter, familyFilter, subfamilyFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      loadGroups({
        limit: rowsPerPage,
        offset: 0,
        brand_code: brandFilter || undefined,
        category_item_code: categoryItemFilter || undefined,
        family_code: familyFilter || undefined,
        subfamily_code: subfamilyFilter || undefined,
        search: searchQuery || undefined,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, rowsPerPage, brandFilter, categoryItemFilter, familyFilter, subfamilyFilter, loadGroups]);

  // Cross-filtered options from API
  type FilterOption = { code: string; name: string; count: number };
  const [brands, setBrands] = useState<FilterOption[]>([]);
  const [categoryItems, setCategoryItems] = useState<FilterOption[]>([]);
  const [families, setFamilies] = useState<FilterOption[]>([]);
  const [subfamilies, setSubfamilies] = useState<FilterOption[]>([]);

  // Fetch filter options whenever active filters change
  useEffect(() => {
    productService.getProductFilters({
      brand_code: brandFilter || undefined,
      category_item_code: categoryItemFilter || undefined,
      family_code: familyFilter || undefined,
      subfamily_code: subfamilyFilter || undefined,
      search: searchQuery || undefined,
    }).then((data) => {
      setBrands(data.brands);
      setCategoryItems(data.category_items);
      setFamilies(data.families);
      setSubfamilies(data.subfamilies);
    }).catch(() => {});
  }, [brandFilter, categoryItemFilter, familyFilter, subfamilyFilter, searchQuery]);

  // Client-side sort (grouped)
  const sortedGroups = useMemo(() => {
    const sorted = [...apiGroups];
    const [field, order] = sortBy.split('-');
    sorted.sort((a, b) => {
      let cmp = 0;
      if (field === 'name') cmp = (a.product_name || '').localeCompare(b.product_name || '');
      else if (field === 'family') cmp = (a.family_name || '').localeCompare(b.family_name || '');
      else if (field === 'price') cmp = (a.min_price || 0) - (b.min_price || 0);
      return order === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [apiGroups, sortBy]);

  const handleGroupClick = (groupKey: string, initialVariantId?: string | null) => {
    // Open the variant picker dialog for this group, optionally preselecting
    // a specific variant (used by the search autocomplete to jump straight
    // to the matching variant).
    setDetailInitialVariantId(initialVariantId || null);
    setDetailProductId(groupKey);
  };

  const handleBrandChange = (value: string) => {
    setBrandFilter(value);
    if (value) {
      const match = brands.find((b) => b.code === value);
      if (match) setFilterNames((prev) => ({ ...prev, brand: match.name }));
    }
    setPage(0);
  };

  const handleCategoryItemChange = (value: string) => {
    setCategoryItemFilter(value);
    if (value) {
      const match = categoryItems.find((c) => c.code === value);
      if (match) setFilterNames((prev) => ({ ...prev, categoryItem: match.name }));
    }
    setPage(0);
  };

  const handleFamilyChange = (value: string) => {
    setFamilyFilter(value);
    setSubfamilyFilter('');
    if (value) {
      const match = families.find((f) => f.code === value);
      if (match) setFilterNames((prev) => ({ ...prev, family: match.name }));
    }
    setPage(0);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setBrandFilter('');
    setCategoryItemFilter('');
    setFamilyFilter('');
    setSubfamilyFilter('');
    setPage(0);
  };

  const activeFilterCount = [brandFilter, categoryItemFilter, familyFilter, subfamilyFilter].filter(Boolean).length;
  const hasActiveFilters = !!(searchQuery || activeFilterCount > 0);

  // Loading skeleton
  if (loading && apiGroups.length === 0) {
    return (
      <Box>
        <Typography variant="h5" component="h1" fontWeight={700} gutterBottom>
          {t('products.title')}
        </Typography>
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <Grid key={i} size={{ xs: 6, sm: 4, md: 3, lg: 3 }}>
              <Box sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
                <Skeleton variant="rectangular" height={240} animation="wave" />
                <Box sx={{ p: 2 }}>
                  <Skeleton variant="text" width="40%" sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="35%" sx={{ mt: 1 }} />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
          {t('products.title')}
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'hidden' }}>
      {/* Promotion Banner Carousel */}
      {promoBanners.length > 0 && (
        <Box sx={{ mb: 3, position: 'relative', borderRadius: 2, overflow: 'hidden', mx: { xs: -2, md: 0 } }}>
          <Box sx={{ position: 'relative', width: '100%', height: { xs: 140, sm: 200, md: 280 }, bgcolor: 'grey.100' }}>
            {promoBanners.map((promo, idx) => (
              <Box
                key={promo.id}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: idx === promoIndex ? 1 : 0,
                  transition: 'opacity 0.6s ease-in-out',
                  pointerEvents: idx === promoIndex ? 'auto' : 'none',
                  cursor: promo.cta_url ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (promo.cta_url) {
                    if (promo.cta_url.startsWith('http')) window.open(promo.cta_url, '_blank', 'noopener');
                    else navigate(promo.cta_url);
                  }
                }}
              >
                <Box
                  component="img"
                  src={promo.image_url}
                  alt={promo.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            ))}
          </Box>
          {/* Nav arrows */}
          {promoBanners.length > 1 && (
            <>
              <IconButton
                onClick={() => setPromoIndex((p) => (p - 1 + promoBanners.length) % promoBanners.length)}
                sx={{
                  position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'white' },
                  width: 36, height: 36,
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={() => setPromoIndex((p) => (p + 1) % promoBanners.length)}
                sx={{
                  position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'white' },
                  width: 36, height: 36,
                }}
              >
                <ChevronRightIcon />
              </IconButton>
              {/* Dots */}
              <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.8 }}>
                {promoBanners.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={() => setPromoIndex(idx)}
                    sx={{
                      width: idx === promoIndex ? 20 : 8, height: 8,
                      borderRadius: 4,
                      bgcolor: idx === promoIndex ? 'white' : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      )}

      {/* Header */}
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('products.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isId ? `Menampilkan ${apiTotal} grup produk` : `Showing ${apiTotal} product groups`}
      </Typography>

      {/* Mobile: search bar */}
      {isMobile && (
        <Box sx={{ mb: 2 }}>
          <ProductSearchAutocomplete
            value={searchQuery}
            onSearchChange={setSearchQuery}
            onVariantSelect={(groupKey, variantId) => handleGroupClick(groupKey, variantId)}
            fullWidth
          />
        </Box>
      )}

      {/* Mobile: filter drawer */}
      <Drawer
        anchor="bottom"
        open={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '80vh' } }}
      >
        <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" fontWeight={700}>{isId ? 'Filter' : 'Filters'}</Typography>
          <IconButton onClick={() => setShowMobileFilters(false)} size="small"><CloseOutlined /></IconButton>
        </Box>
        <Box sx={{ px: 0, pb: 2, overflow: 'auto' }}>
          <SidebarFilters
            brands={brands}
            categoryItems={categoryItems}
            families={families}
            subfamilies={subfamilies}
            brandFilter={brandFilter}
            categoryItemFilter={categoryItemFilter}
            familyFilter={familyFilter}
            subfamilyFilter={subfamilyFilter}
            onBrandChange={(v) => { handleBrandChange(v); setShowMobileFilters(false); }}
            onCategoryItemChange={(v) => { handleCategoryItemChange(v); setShowMobileFilters(false); }}
            onFamilyChange={(v) => { handleFamilyChange(v); setShowMobileFilters(false); }}
            onSubfamilyChange={(v) => { setSubfamilyFilter(v); if (v) { const m = subfamilies.find((s) => s.code === v); if (m) setFilterNames((prev) => ({ ...prev, subfamily: m.name })); } setPage(0); setShowMobileFilters(false); }}
            onReset={() => { resetFilters(); setShowMobileFilters(false); }}
            activeFilterCount={activeFilterCount}
          />
        </Box>
      </Drawer>

      <Grid container spacing={3}>
        {/* Desktop: Sidebar */}
        {!isMobile && (
          <Grid size={{ md: 3 }}>
            <SidebarFilters
              brands={brands}
              categoryItems={categoryItems}
              families={families}
              subfamilies={subfamilies}
              brandFilter={brandFilter}
              categoryItemFilter={categoryItemFilter}
              familyFilter={familyFilter}
              subfamilyFilter={subfamilyFilter}
              onBrandChange={handleBrandChange}
              onCategoryItemChange={handleCategoryItemChange}
              onFamilyChange={handleFamilyChange}
              onSubfamilyChange={(v) => { setSubfamilyFilter(v); if (v) { const m = subfamilies.find((s) => s.code === v); if (m) setFilterNames((prev) => ({ ...prev, subfamily: m.name })); } setPage(0); }}
              onReset={resetFilters}
              activeFilterCount={activeFilterCount}
            />
          </Grid>
        )}

        {/* Products */}
        <Grid size={{ xs: 12, md: 9 }}>
          {/* Toolbar: search (desktop) + sort + view toggle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {/* Search (desktop only) */}
            {!isMobile && (
              <ProductSearchAutocomplete
                value={searchQuery}
                onSearchChange={setSearchQuery}
                onVariantSelect={(groupKey, variantId) => handleGroupClick(groupKey, variantId)}
                width={460}
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
              {/* Sort pills */}
              {!isMobile && (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[
                    { value: 'name-asc', label: 'A-Z' },
                    { value: 'name-desc', label: 'Z-A' },
                    { value: 'price-asc', label: isId ? 'Termurah' : 'Lowest' },
                    { value: 'price-desc', label: isId ? 'Termahal' : 'Highest' },
                  ].map((opt) => (
                    <Chip
                      key={opt.value}
                      label={opt.label}
                      size="small"
                      onClick={() => setSortBy(opt.value)}
                      sx={{
                        fontWeight: sortBy === opt.value ? 600 : 400,
                        bgcolor: sortBy === opt.value ? '#ee4d2d' : '#f5f5f5',
                        color: sortBy === opt.value ? 'white' : 'text.primary',
                        border: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                          bgcolor: sortBy === opt.value ? '#d73211' : '#eee',
                        },
                      }}
                    />
                  ))}
                </Box>
              )}

              {/* Mobile sort dropdown */}
              {isMobile && (
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    startAdornment={<SwapVert sx={{ fontSize: 16, mr: 0.5 }} />}
                    sx={{ fontSize: '0.8rem' }}
                  >
                    <MenuItem value="name-asc">A-Z</MenuItem>
                    <MenuItem value="name-desc">Z-A</MenuItem>
                    <MenuItem value="price-asc">{isId ? 'Termurah' : 'Lowest'}</MenuItem>
                    <MenuItem value="price-desc">{isId ? 'Termahal' : 'Highest'}</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* View toggle */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, v) => v && setViewMode(v)}
                size="small"
              >
                <ToggleButton value="grid" sx={{ px: 1 }}>
                  <GridView sx={{ fontSize: 20 }} />
                </ToggleButton>
                <ToggleButton value="list" sx={{ px: 1 }}>
                  <ViewList sx={{ fontSize: 20 }} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.75rem' }}>
                {isId ? 'Filter aktif:' : 'Active filters:'}
              </Typography>
              {searchQuery && (
                <Chip
                  size="small"
                  label={`"${searchQuery}"`}
                  onDelete={() => setSearchQuery('')}
                  sx={{ bgcolor: '#fff3f0', color: '#ee4d2d', '& .MuiChip-deleteIcon': { color: '#ee4d2d' } }}
                />
              )}
              {brandFilter && (
                <Chip
                  size="small"
                  label={brands.find((b) => b.code === brandFilter)?.name || filterNames.brand || brandFilter}
                  onDelete={() => setBrandFilter('')}
                  sx={{ bgcolor: '#fff3f0', color: '#ee4d2d', '& .MuiChip-deleteIcon': { color: '#ee4d2d' } }}
                />
              )}
              {categoryItemFilter && (
                <Chip
                  size="small"
                  label={categoryItems.find((c) => c.code === categoryItemFilter)?.name || filterNames.categoryItem || categoryItemFilter}
                  onDelete={() => setCategoryItemFilter('')}
                  sx={{ bgcolor: '#fff3f0', color: '#ee4d2d', '& .MuiChip-deleteIcon': { color: '#ee4d2d' } }}
                />
              )}
              {familyFilter && (
                <Chip
                  size="small"
                  label={families.find((f) => f.code === familyFilter)?.name || filterNames.family || familyFilter}
                  onDelete={() => setFamilyFilter('')}
                  sx={{ bgcolor: '#fff3f0', color: '#ee4d2d', '& .MuiChip-deleteIcon': { color: '#ee4d2d' } }}
                />
              )}
              {subfamilyFilter && (
                <Chip
                  size="small"
                  label={subfamilies.find((sf) => sf.code === subfamilyFilter)?.name || filterNames.subfamily || subfamilyFilter}
                  onDelete={() => setSubfamilyFilter('')}
                  sx={{ bgcolor: '#fff3f0', color: '#ee4d2d', '& .MuiChip-deleteIcon': { color: '#ee4d2d' } }}
                />
              )}
              <Button
                size="small"
                onClick={resetFilters}
                sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#ee4d2d', minWidth: 'auto', px: 1 }}
              >
                {isId ? 'Hapus Semua' : 'Clear All'}
              </Button>
            </Box>
          )}

          {/* Product Grid / List */}
          {sortedGroups.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                bgcolor: '#fafafa',
                borderRadius: 3,
                border: '1px dashed',
                borderColor: 'grey.300',
              }}
            >
              <Box sx={{ mb: 2 }}>
                <FormatPaint sx={{ fontSize: 72, color: 'grey.300' }} />
              </Box>
              <Typography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                {isId ? 'Produk tidak ditemukan' : 'No products found'}
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3, maxWidth: 360, mx: 'auto' }}>
                {isId
                  ? 'Coba ubah filter atau kata kunci pencarian Anda untuk menemukan produk yang Anda cari'
                  : 'Try adjusting your filters or search terms to find the products you are looking for'}
              </Typography>
              {hasActiveFilters && (
                <Button
                  variant="contained"
                  onClick={resetFilters}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    bgcolor: '#ee4d2d',
                    '&:hover': { bgcolor: '#d73211' },
                    borderRadius: 2,
                    px: 4,
                  }}
                >
                  {isId ? 'Hapus Semua Filter' : 'Clear All Filters'}
                </Button>
              )}
            </Box>
          ) : viewMode === 'grid' ? (
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              {sortedGroups.map((group) => (
                <Grid key={group.group_key} size={{ xs: 6, sm: 4, md: 4, lg: 3 }}>
                  <GroupedProductCardGrid group={group} onGroupClick={handleGroupClick} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              {sortedGroups.map((group) => (
                <GroupedProductCardList key={group.group_key} group={group} onGroupClick={handleGroupClick} />
              ))}
            </Box>
          )}

          {/* Load More / Pagination */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {isId
                ? `Menampilkan ${Math.min((page + 1) * rowsPerPage, apiTotal)} dari ${apiTotal} grup produk`
                : `Showing ${Math.min((page + 1) * rowsPerPage, apiTotal)} of ${apiTotal} product groups`}
            </Typography>
            {(page + 1) * rowsPerPage < apiTotal && (
              <Button
                variant="outlined"
                onClick={() => setPage((p) => p + 1)}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 6,
                  py: 1,
                  borderRadius: 2,
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': { borderColor: '#ee4d2d', color: '#ee4d2d' },
                }}
              >
                {loading ? t('common.loading') : isId ? 'Lihat Lebih Banyak' : 'Load More'}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Product Detail Dialog */}
      <React.Suspense fallback={null}>
        <GroupedProductDetailDialog
          open={!!detailProductId}
          onClose={() => {
            setDetailProductId(null);
            setDetailInitialVariantId(null);
          }}
          groupKey={detailProductId}
          initialVariantId={detailInitialVariantId}
        />
      </React.Suspense>

      {/* Mobile sticky bottom bar */}
      {isMobile && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1100,
            display: 'flex',
            borderTop: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 0,
          }}
        >
          <Button
            fullWidth
            startIcon={
              <Badge badgeContent={activeFilterCount} color="error">
                <TuneOutlined />
              </Badge>
            }
            onClick={() => setShowMobileFilters(true)}
            sx={{
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              borderRight: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 0,
              color: 'text.primary',
            }}
          >
            {isId ? 'Filter' : 'Filter'}
          </Button>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              startAdornment={<SwapVert sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />}
              sx={{
                height: '100%',
                borderRadius: 0,
                '& fieldset': { border: 'none' },
                fontWeight: 600,
                fontSize: '0.85rem',
                pl: 1.5,
              }}
            >
              <MenuItem value="name-asc">A-Z</MenuItem>
              <MenuItem value="name-desc">Z-A</MenuItem>
              <MenuItem value="price-asc">{isId ? 'Termurah' : 'Lowest'}</MenuItem>
              <MenuItem value="price-desc">{isId ? 'Termahal' : 'Highest'}</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}
    </Box>
  );
};

export default ProductsPage;
